/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var _licenseKeyValidityState;
import { defaultParseToDateTime } from './DateTimeDefault';
import { instanceOfSimpleDate } from './DateTimeHelper';
import { AlwaysDense } from './DependencyGraph/AddressMapping/ChooseAddressMappingPolicy';
import { ConfigValueEmpty, ConfigValueTooBigError, ConfigValueTooSmallError, ExpectedOneOfValuesError, ExpectedValueOfTypeError } from './errors';
import { defaultStringifyDateTime, defaultStringifyDuration } from './format/format';
import { HyperFormula } from './HyperFormula';
import { checkLicenseKeyValidity } from './helpers/licenseKeyValidator';
const PossibleGPUModeString = ['gpu', 'cpu', 'dev'];
export class Config {
    constructor({ accentSensitive, caseSensitive, caseFirst, chooseAddressMappingPolicy, dateFormats, timeFormats, functionArgSeparator, decimalSeparator, thousandSeparator, language, licenseKey, functionPlugins, gpujs, gpuMode, ignorePunctuation, leapYear1900, localeLang, smartRounding, matrixDetection, matrixDetectionThreshold, evaluateNullToZero, nullYear, parseDateTime, stringifyDateTime, stringifyDuration, precisionEpsilon, precisionRounding, useColumnIndex, binarySearchThreshold, nullDate, useStats, undoLimit, useRegularExpressions, useWildcards, matchWholeCell, maxRows, maxColumns, currencySymbol, } = {}) {
        /**
         * Set automatically based on licenseKey checking result.
         *
         * @internal
         */
        _licenseKeyValidityState.set(this, void 0);
        this.accentSensitive = this.valueFromParam(accentSensitive, 'boolean', 'accentSensitive');
        this.caseSensitive = this.valueFromParam(caseSensitive, 'boolean', 'caseSensitive');
        this.caseFirst = this.valueFromParam(caseFirst, ['upper', 'lower', 'false'], 'caseFirst');
        this.ignorePunctuation = this.valueFromParam(ignorePunctuation, 'boolean', 'ignorePunctuation');
        this.chooseAddressMappingPolicy = chooseAddressMappingPolicy !== null && chooseAddressMappingPolicy !== void 0 ? chooseAddressMappingPolicy : Config.defaultConfig.chooseAddressMappingPolicy;
        this.dateFormats = this.valueFromParamCheck(dateFormats, Array.isArray, 'array', 'dateFormats');
        this.timeFormats = this.valueFromParamCheck(timeFormats, Array.isArray, 'array', 'timeFormats');
        this.functionArgSeparator = this.valueFromParam(functionArgSeparator, 'string', 'functionArgSeparator');
        this.decimalSeparator = this.valueFromParam(decimalSeparator, ['.', ','], 'decimalSeparator');
        this.language = this.valueFromParam(language, 'string', 'language');
        this.licenseKey = this.valueFromParam(licenseKey, 'string', 'licenseKey');
        __classPrivateFieldSet(this, _licenseKeyValidityState, checkLicenseKeyValidity(this.licenseKey));
        this.thousandSeparator = this.valueFromParam(thousandSeparator, ['', ',', ' ', '.'], 'thousandSeparator');
        this.localeLang = this.valueFromParam(localeLang, 'string', 'localeLang');
        this.functionPlugins = functionPlugins !== null && functionPlugins !== void 0 ? functionPlugins : Config.defaultConfig.functionPlugins;
        this.gpujs = gpujs !== null && gpujs !== void 0 ? gpujs : Config.defaultConfig.gpujs;
        this.gpuMode = this.valueFromParam(gpuMode, PossibleGPUModeString, 'gpuMode');
        this.smartRounding = this.valueFromParam(smartRounding, 'boolean', 'smartRounding');
        this.matrixDetection = this.valueFromParam(matrixDetection, 'boolean', 'matrixDetection');
        this.matrixDetectionThreshold = this.valueFromParam(matrixDetectionThreshold, 'number', 'matrixDetectionThreshold');
        this.validateNumberToBeAtLeast(this.matrixDetectionThreshold, 'matrixDetectionThreshold', 1);
        this.evaluateNullToZero = this.valueFromParam(evaluateNullToZero, 'boolean', 'evaluateNullToZero');
        this.nullYear = this.valueFromParam(nullYear, 'number', 'nullYear');
        this.validateNumberToBeAtLeast(this.nullYear, 'nullYear', 0);
        this.validateNumberToBeAtMost(this.nullYear, 'nullYear', 100);
        this.precisionRounding = this.valueFromParam(precisionRounding, 'number', 'precisionRounding');
        this.validateNumberToBeAtLeast(this.precisionRounding, 'precisionRounding', 0);
        this.precisionEpsilon = this.valueFromParam(precisionEpsilon, 'number', 'precisionEpsilon');
        this.validateNumberToBeAtLeast(this.precisionEpsilon, 'precisionEpsilon', 0);
        this.useColumnIndex = this.valueFromParam(useColumnIndex, 'boolean', 'useColumnIndex');
        this.useStats = this.valueFromParam(useStats, 'boolean', 'useStats');
        this.binarySearchThreshold = this.valueFromParam(binarySearchThreshold, 'number', 'binarySearchThreshold');
        this.validateNumberToBeAtLeast(this.binarySearchThreshold, 'binarySearchThreshold', 1);
        this.parseDateTime = this.valueFromParam(parseDateTime, 'function', 'parseDateTime');
        this.stringifyDateTime = this.valueFromParam(stringifyDateTime, 'function', 'stringifyDateTime');
        this.stringifyDuration = this.valueFromParam(stringifyDuration, 'function', 'stringifyDuration');
        this.translationPackage = HyperFormula.getLanguage(this.language);
        this.errorMapping = this.translationPackage.buildErrorMapping();
        this.nullDate = this.valueFromParamCheck(nullDate, instanceOfSimpleDate, 'IDate', 'nullDate');
        this.leapYear1900 = this.valueFromParam(leapYear1900, 'boolean', 'leapYear1900');
        this.undoLimit = this.valueFromParam(undoLimit, 'number', 'undoLimit');
        this.useRegularExpressions = this.valueFromParam(useRegularExpressions, 'boolean', 'useRegularExpressions');
        this.useWildcards = this.valueFromParam(useWildcards, 'boolean', 'useWildcards');
        this.matchWholeCell = this.valueFromParam(matchWholeCell, 'boolean', 'matchWholeCell');
        this.validateNumberToBeAtLeast(this.undoLimit, 'undoLimit', 0);
        this.maxRows = this.valueFromParam(maxRows, 'number', 'maxRows');
        this.validateNumberToBeAtLeast(this.maxRows, 'maxRows', 1);
        this.maxColumns = this.valueFromParam(maxColumns, 'number', 'maxColumns');
        this.currencySymbol = this.valueFromParamCheck(currencySymbol, Array.isArray, 'array', 'currencySymbol');
        this.currencySymbol.forEach((val) => {
            if (typeof val !== 'string') {
                throw new ExpectedValueOfTypeError('string[]', 'currencySymbol');
            }
            if (val === '') {
                throw new ConfigValueEmpty('currencySymbol');
            }
        });
        this.validateNumberToBeAtLeast(this.maxColumns, 'maxColumns', 1);
        this.checkIfParametersNotInConflict({ value: this.decimalSeparator, name: 'decimalSeparator' }, { value: this.functionArgSeparator, name: 'functionArgSeparator' }, { value: this.thousandSeparator, name: 'thousandSeparator' });
    }
    /**
     * Proxied property to its private counterpart. This makes the property
     * as accessible as the other Config options but without ability to change the value.
     *
     * @internal
     */
    get licenseKeyValidityState() {
        return __classPrivateFieldGet(this, _licenseKeyValidityState);
    }
    getConfig() {
        return this;
    }
    mergeConfig(init) {
        const mergedConfig = Object.assign({}, this.getConfig(), init);
        return new Config(mergedConfig);
    }
    warnDeprecatedIfUsed(inputValue, paramName, fromVersion, replacementName) {
        if (inputValue !== undefined) {
            console.warn(`${paramName} option is deprecated since ${fromVersion}, please use ${replacementName}`);
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueFromParam(inputValue, expectedType, paramName) {
        if (typeof inputValue === 'undefined') {
            return Config.defaultConfig[paramName];
        }
        else if (typeof expectedType === 'string') {
            if (typeof inputValue === expectedType) {
                return inputValue;
            }
            else {
                throw new ExpectedValueOfTypeError(expectedType, paramName);
            }
        }
        else {
            if (expectedType.includes(inputValue)) {
                return inputValue;
            }
            else {
                throw new ExpectedOneOfValuesError(expectedType.map((val) => '\'' + val + '\'').join(' '), paramName);
            }
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueFromParamCheck(inputValue, typeCheck, expectedType, paramName) {
        if (typeCheck(inputValue)) {
            return inputValue;
        }
        else if (typeof inputValue === 'undefined') {
            return Config.defaultConfig[paramName];
        }
        else {
            throw new ExpectedValueOfTypeError(expectedType, paramName);
        }
    }
    checkIfParametersNotInConflict(...params) {
        const valuesMap = new Map();
        params.forEach((param) => {
            const names = valuesMap.get(param.value) || [];
            names.push(param.name);
            valuesMap.set(param.value, names);
        });
        const duplicates = [];
        for (const entry of valuesMap.values()) {
            if (entry.length > 1) {
                duplicates.push(entry);
            }
        }
        if (duplicates.length > 0) {
            duplicates.forEach(entry => entry.sort());
            const paramNames = duplicates.map(entry => `[${entry}]`).join('; ');
            throw new Error(`Config initialization failed. Parameters in conflict: ${paramNames}`);
        }
    }
    validateNumberToBeAtLeast(value, paramName, minimum) {
        if (value < minimum) {
            throw new ConfigValueTooSmallError(paramName, minimum);
        }
    }
    validateNumberToBeAtMost(value, paramName, maximum) {
        if (value > maximum) {
            throw new ConfigValueTooBigError(paramName, maximum);
        }
    }
}
_licenseKeyValidityState = new WeakMap();
Config.defaultConfig = {
    accentSensitive: false,
    caseSensitive: false,
    caseFirst: 'lower',
    ignorePunctuation: false,
    chooseAddressMappingPolicy: new AlwaysDense(),
    dateFormats: ['DD/MM/YYYY', 'DD/MM/YY'],
    timeFormats: ['hh:mm', 'hh:mm:ss.sss'],
    functionArgSeparator: ',',
    decimalSeparator: '.',
    thousandSeparator: '',
    language: 'enGB',
    licenseKey: '',
    functionPlugins: [],
    gpujs: undefined,
    gpuMode: 'gpu',
    leapYear1900: false,
    smartRounding: true,
    localeLang: 'en',
    matrixDetection: true,
    matrixDetectionThreshold: 100,
    evaluateNullToZero: false,
    nullYear: 30,
    parseDateTime: defaultParseToDateTime,
    stringifyDateTime: defaultStringifyDateTime,
    stringifyDuration: defaultStringifyDuration,
    precisionEpsilon: 1e-13,
    precisionRounding: 14,
    useColumnIndex: false,
    useStats: false,
    binarySearchThreshold: 20,
    nullDate: { year: 1899, month: 12, day: 30 },
    undoLimit: 20,
    useRegularExpressions: false,
    useWildcards: true,
    matchWholeCell: true,
    maxRows: 40000,
    maxColumns: 18278,
    currencySymbol: ['$'],
};
